import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Rating,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Avatar,
  AppBar,
  Toolbar,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Link,
  Badge,
  Stack
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CheckCircle as ResolveIcon,
  Cancel as UnresolveIcon,
  Feedback as FeedbackIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Clear as ClearIcon,
  Menu as MenuIcon,
  AccountCircle,
  Dashboard as DashboardIcon,
  Call as CallIcon,
  PhoneForwarded as OutboundIcon,
  ExitToApp as LogoutIcon,
  People as PeopleIcon,
  Science as TestIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  PlayArrow as PlayIcon,
  Description as SummaryIcon,
  Subject as TranscriptIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Storage as DataIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  BatchPrediction as BatchesIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { feedbackService, authService, callsService, organizationService } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';

const FEEDBACK_TYPE_COLORS = {
  general: {
    backgroundColor: '#f5f5f5',
    color: '#616161',
    border: '1px solid #e0e0e0'
  },
  quality: {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32',
    border: '1px solid #c8e6c9'
  },
  accuracy: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    border: '1px solid #bbdefb'
  },
  improvement: {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    border: '1px solid #ffcc02'
  }
};

const FEEDBACK_TYPE_LABELS = {
  general: 'General',
  quality: 'Quality',
  accuracy: 'Accuracy',
  improvement: 'Improvement'
};

const FeedbackManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Filters
  const [feedbackType, setFeedbackType] = useState('');
  const [isResolved, setIsResolved] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // UI State
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState(null);

  // Call details modal state
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const currencySymbol = process.env.REACT_APP_CURRENCY_SYMBOL || '';
  
  const userRole = authService.getUserRole();
  const isSuperAdmin = userRole === 'super_admin';
  const canViewAnalytics = userRole === 'super_admin' || userRole === 'admin';

  useEffect(() => {
    if (tabValue === 0) {
      loadFeedback();
    } else if (tabValue === 1 && canViewAnalytics) {
      loadAnalytics();
    }
  }, [page, feedbackType, isResolved, tabValue]);

  // Load current organization on component mount
  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
  }, []);

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const response = await feedbackService.getAllFeedback(
        page, 
        10, 
        feedbackType || null, 
        isResolved === '' ? null : isResolved === 'true'
      );
      setFeedback(response.feedback || []);
      setTotalPages(response.total_pages || 1);
      setTotalCount(response.total_count || 0);
    } catch (error) {
      toast.error(error.message || 'Failed to load feedback');
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (!canViewAnalytics) return;
    
    setAnalyticsLoading(true);
    try {
      const response = await feedbackService.getFeedbackAnalytics();
      setAnalytics(response);
    } catch (error) {
      toast.error(error.message || 'Failed to load analytics');
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleMenuClick = (event, feedbackItem) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFeedback(feedbackItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFeedback(null);
  };

  const handleResolveToggle = async (feedbackItem) => {
    if (!isSuperAdmin) {
      toast.error('Only super admins can resolve feedback');
      return;
    }

    try {
      const newResolvedState = !feedbackItem.is_resolved;
      await feedbackService.resolveFeedback(feedbackItem.id, newResolvedState);
      toast.success(`Feedback ${newResolvedState ? 'resolved' : 'unresolved'} successfully!`);
      loadFeedback();
    } catch (error) {
      toast.error(error.message || 'Failed to update feedback status');
    }
    handleMenuClose();
  };

  const handleFilterChange = () => {
    setPage(1);
    loadFeedback();
  };

  const handleClearFilters = () => {
    setFeedbackType('');
    setIsResolved('');
    setSearchTerm('');
    setPage(1);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleOpenProfileMenu = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setProfileAnchorEl(null);
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Helper function to format field names
  const formatFieldName = (fieldName) => {
    // Replace underscores with spaces and convert to sentence case
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...((userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator') && currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }] : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator' ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const drawerWidth = 240;

  // Fetch call details
  const fetchCallDetails = async (callId) => {
    try {
      setCallDetailsLoading(true);
      const detailsData = await callsService.getCallDetails(callId);
      if (detailsData.result && detailsData.result.length > 0) {
        setCallDetails(detailsData.result[0]);
      } else {
        setCallDetails(null);
        toast.error('Call details not found');
      }
    } catch (err) {
      console.error('Error fetching call details:', err);
      setCallDetails(null);
      toast.error('Failed to load call details');
    } finally {
      setCallDetailsLoading(false);
    }
  };

  // Handle call ID click
  const handleCallClick = async (callId) => {
    setSelectedCallId(callId);
    setModalOpen(true);
    await fetchCallDetails(callId);
  };

  // Handle modal close
  const handleCallModalClose = () => {
    setModalOpen(false);
    setTimeout(() => {
      setSelectedCallId(null);
      setCallDetails(null);
    }, 200);
  };

  const drawer = (
    <Box sx={{ 
      width: drawerWidth, 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
      borderRight: '1px solid rgba(102, 117, 140, 0.12)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        pt: '12px',
        pb: '12px',
        px: 2,
        borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
        background: '#2541b2',
        color: 'white',
        position: 'relative'
      }}>
        {/* Side-by-side logos */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box
            component="img"
            src="/telly_logo.png"
            alt="TellyFlow"
            sx={{
              width: 'auto',
              height: '32px',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)'
            }}
          />
          <Box sx={{ 
            width: '1px', 
            height: '24px', 
            backgroundColor: 'rgba(255, 255, 255, 0.3)' 
          }} />
          <Box
            sx={{
              width: '60px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {COMPANY_LOGO && (
              <Box
                component="img"
                src={COMPANY_LOGO}
                alt={`${COMPANY_NAME || 'Company'} Logo`}
                sx={{
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {drawerItems.map((item) => {
            const isActive = item.text === 'Feedback';
            return (
              <ListItem 
                button 
                key={item.text} 
                onClick={() => navigate(item.path)}
                sx={{ 
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': {
                    transform: 'translateX(1px)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'all 0.2s ease-in-out'
                  }} 
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(102, 117, 140, 0.08)',
        background: 'rgba(102, 117, 140, 0.02)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              transform: 'translateX(2px)',
              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.15)'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout" 
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'error.main'
            }}
          />
        </ListItem>
      </Box>
    </Box>
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (minutes) => {
    const totalMinutes = Math.floor(minutes);
    const seconds = Math.round((minutes - totalMinutes) * 60);
    return `${totalMinutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined || isNaN(Number(cost))) return 'N/A';
    return `${currencySymbol}${Number(cost).toFixed(2)}`;
  };

  const filteredFeedback = feedback.filter(item => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.feedback_text.toLowerCase().includes(searchLower) ||
      item.user_email.toLowerCase().includes(searchLower) ||
      item.call_id.toLowerCase().includes(searchLower)
    );
  });

  const renderAnalytics = () => {
    if (analyticsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!analytics) {
      return (
        <Alert severity="error">
          Failed to load analytics data
        </Alert>
      );
    }

    return (
      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '1px solid rgba(102, 117, 140, 0.12)',
            borderRadius: 2,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0px 8px 24px rgba(67, 97, 238, 0.12)',
              borderColor: 'rgba(67, 97, 238, 0.25)'
            }
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1.3
                }}>
                  Total<br />Feedback
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'primary.main', 
                  width: { xs: 40, sm: 44 }, 
                  height: { xs: 40, sm: 44 },
                  boxShadow: '0 3px 8px rgba(67, 97, 238, 0.25)'
                }}>
                  <FeedbackIcon fontSize="small" />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                color: 'text.primary'
              }}>
                {analytics.total_feedback}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ 
                fontSize: '0.7rem',
                fontWeight: 500
              }}>
                All time feedback received
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '1px solid rgba(102, 117, 140, 0.12)',
            borderRadius: 2,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0px 8px 24px rgba(255, 193, 102, 0.12)',
              borderColor: 'rgba(255, 193, 102, 0.25)'
            }
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1.3
                }}>
                  Average<br />Rating
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'warning.main', 
                  width: { xs: 40, sm: 44 }, 
                  height: { xs: 40, sm: 44 },
                  boxShadow: '0 3px 8px rgba(255, 193, 102, 0.25)'
                }}>
                  <StarIcon fontSize="small" />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                color: 'text.primary'
              }}>
                {analytics.average_rating ? analytics.average_rating.toFixed(1) : 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ 
                fontSize: '0.7rem',
                fontWeight: 500
              }}>
                Out of 5 stars
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '1px solid rgba(102, 117, 140, 0.12)',
            borderRadius: 2,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0px 8px 24px rgba(23, 162, 184, 0.12)',
              borderColor: 'rgba(23, 162, 184, 0.25)'
            }
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1.3
                }}>
                  Recent<br />Feedback
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'info.main', 
                  width: { xs: 40, sm: 44 }, 
                  height: { xs: 40, sm: 44 },
                  boxShadow: '0 3px 8px rgba(23, 162, 184, 0.25)'
                }}>
                  <TrendingUpIcon fontSize="small" />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                color: 'text.primary'
              }}>
                {analytics.recent_feedback_count}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ 
                fontSize: '0.7rem',
                fontWeight: 500
              }}>
                Last 30 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '1px solid rgba(102, 117, 140, 0.12)',
            borderRadius: 2,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0px 8px 24px rgba(6, 214, 160, 0.12)',
              borderColor: 'rgba(6, 214, 160, 0.25)'
            }
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1.3
                }}>
                  Resolution<br />Rate
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'success.main', 
                  width: { xs: 40, sm: 44 }, 
                  height: { xs: 40, sm: 44 },
                  boxShadow: '0 3px 8px rgba(6, 214, 160, 0.25)'
                }}>
                  <AnalyticsIcon fontSize="small" />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                color: 'text.primary'
              }}>
                {analytics.resolved_percentage}%
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ 
                fontSize: '0.7rem',
                fontWeight: 500
              }}>
                Issues resolved
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Feedback by Type */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Feedback by Type
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(analytics.feedback_by_type).map(([type, count]) => (
                <Box key={type} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={FEEDBACK_TYPE_LABELS[type] || type}
                      size="small"
                      variant="filled"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        height: 28,
                        px: 1.5,
                        borderRadius: 1.5,
                        textTransform: 'capitalize',
                        letterSpacing: '0.25px',
                        ...FEEDBACK_TYPE_COLORS[type] || FEEDBACK_TYPE_COLORS.general,
                        '& .MuiChip-label': {
                          color: (FEEDBACK_TYPE_COLORS[type] || FEEDBACK_TYPE_COLORS.general)?.color
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>

        {/* Top Feedback Calls */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Most Feedback Calls
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {analytics.top_feedback_calls.map((call, index) => (
                <Box key={call.call_id} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <Typography variant="caption">{index + 1}</Typography>
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {call.call_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {call.feedback_count} feedback • {call.average_rating ? `${call.average_rating.toFixed(1)} ⭐` : 'No rating'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
              {analytics.top_feedback_calls.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No feedback data available
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderFeedbackList = () => {
    return (
      <Box>
        {/* Filters */}
        <Card sx={{ 
          mb: 3, 
          border: '1px solid rgba(102, 117, 140, 0.12)',
          borderRadius: 2,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)'
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1rem'
            }}>
              Filter Feedback
            </Typography>
            <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={feedbackType}
                  label="Type"
                  onChange={(e) => setFeedbackType(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {Object.entries(FEEDBACK_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={isResolved}
                  label="Status"
                  onChange={(e) => setIsResolved(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Resolved</MenuItem>
                  <MenuItem value="false">Unresolved</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleFilterChange}
                  startIcon={<FilterIcon />}
                  size="small"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  size="small"
                >
                  Clear
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="body2" color="text.secondary">
                {totalCount} total feedback
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

        {/* Feedback Table */}
        <Card sx={{ 
          border: '1px solid rgba(102, 117, 140, 0.12)',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)'
        }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ 
              p: { xs: 2, sm: 2.5 },
              borderBottom: '1px solid rgba(102, 117, 140, 0.12)',
              background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.02) 0%, rgba(67, 97, 238, 0.01) 100%)'
            }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '1.125rem', sm: '1.25rem' }
              }}>
                All Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ 
                mt: 0.5,
                fontSize: '0.8rem'
              }}>
                Customer feedback and ratings for your AI calling agents
              </Typography>
            </Box>
            
            <Box sx={{ overflow: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ 
                    '& th': { 
                      borderBottom: '2px solid rgba(102, 117, 140, 0.12)',
                      backgroundColor: 'rgba(67, 97, 238, 0.02)',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1.5, sm: 2 }
                    }
                  }}>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      User
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Call ID
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Rating
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Feedback
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredFeedback.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No feedback found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFeedback.map((item) => (
                    <TableRow 
                      key={item.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'rgba(67, 97, 238, 0.02)',
                          '& td': {
                            borderColor: 'rgba(67, 97, 238, 0.1)'
                          }
                        },
                        '&:last-child td': { 
                          borderBottom: 0 
                        },
                        '& td': {
                          py: { xs: 1.5, sm: 2 },
                          px: { xs: 1.5, sm: 2 },
                          borderBottom: '1px solid rgba(102, 117, 140, 0.08)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.8rem' }}>
                            {item.user_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleCallClick(item.call_id)}
                            sx={{ 
                              fontFamily: 'monospace',
                              textDecoration: 'none',
                              color: 'primary.main',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {item.call_id}
                          </Link>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={FEEDBACK_TYPE_LABELS[item.feedback_type]}
                          size="small"
                          variant="filled"
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            height: 28,
                            px: 1.5,
                            borderRadius: 1.5,
                            textTransform: 'capitalize',
                            letterSpacing: '0.25px',
                            ...FEEDBACK_TYPE_COLORS[item.feedback_type],
                            '& .MuiChip-label': {
                              color: FEEDBACK_TYPE_COLORS[item.feedback_type]?.color
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {item.rating ? (
                          <Rating value={item.rating} readOnly size="small" />
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            No rating
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            fontSize: '0.8rem',
                            lineHeight: 1.4
                          }}
                        >
                          {item.feedback_text}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={item.is_resolved ? 'Resolved' : 'Unresolved'}
                            size="small"
                            variant="filled"
                            sx={{ 
                              fontSize: '0.7rem', 
                              fontWeight: 600,
                              height: 28,
                              px: 1.5,
                              borderRadius: 1.5,
                              textTransform: 'capitalize',
                              letterSpacing: '0.25px',
                              ...(item.is_resolved ? {
                                backgroundColor: '#e8f5e8',
                                color: '#2e7d32',
                                border: '1px solid #c8e6c9',
                                '& .MuiChip-label': {
                                  color: '#2e7d32'
                                }
                              } : {
                                backgroundColor: '#fff3e0',
                                color: '#f57c00',
                                border: '1px solid #ffcc02',
                                '& .MuiChip-label': {
                                  color: '#f57c00'
                                }
                              })
                            }}
                          />
                          {item.is_resolved && item.resolved_by_email && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              by {item.resolved_by_email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {formatDate(item.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isSuperAdmin && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, item)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(67, 97, 238, 0.08)'
                              }
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Sidebar drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen && !isMobile ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{
            borderBottom: '1px solid rgba(102, 117, 140, 0.12)',
            backgroundColor: 'background.paper',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Toolbar sx={{ py: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{ 
                mr: 2,
                '&:hover': {
                  backgroundColor: 'rgba(67, 97, 238, 0.08)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }}>
              Feedback Management
            </Typography>
            
            {/* Organization Switcher Button */}
            {currentOrganization && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchOrganization}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(67, 97, 238, 0.3)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(67, 97, 238, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                {currentOrganization.name}
              </Button>
            )}
            
            <IconButton 
              color="inherit" 
              onClick={handleOpenProfileMenu}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(67, 97, 238, 0.08)'
                }
              }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleCloseProfileMenu}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                  border: '1px solid rgba(102, 117, 140, 0.12)'
                }
              }}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Feedback Management Content */}
        <Container maxWidth="xl" sx={{ mt: 1.5, mb: 4, px: { xs: 1.5, sm: 2.5 } }}>
          {/* Welcome section */}
          <Box sx={{ 
            mb: 2.5, 
            p: { xs: 2, sm: 2.5 },
            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.06) 0%, rgba(67, 97, 238, 0.02) 100%)',
            borderRadius: 2,
            border: '1px solid rgba(67, 97, 238, 0.12)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #4361ee 0%, #738bff 100%)'
            }
          }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ 
              fontWeight: 700, 
              color: 'text.primary',
              mb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}>
              Feedback Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.5,
              maxWidth: '500px'
            }}>
              Monitor and manage customer feedback to improve your AI calling agents performance
            </Typography>
          </Box>

          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider', 
            mb: 3,
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 1,
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)'
          }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: 'primary.main'
                  }
                }
              }}
            >
              <Tab label="All Feedback" />
              {canViewAnalytics && <Tab label="Analytics" />}
            </Tabs>
          </Box>

          {tabValue === 0 && renderFeedbackList()}
          {tabValue === 1 && canViewAnalytics && renderAnalytics()}

          {/* Context Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {selectedFeedback && isSuperAdmin && (
              <MenuItem onClick={() => handleResolveToggle(selectedFeedback)}>
                {selectedFeedback.is_resolved ? (
                  <>
                    <UnresolveIcon sx={{ mr: 1 }} fontSize="small" />
                    Mark as Unresolved
                  </>
                ) : (
                  <>
                    <ResolveIcon sx={{ mr: 1 }} fontSize="small" />
                    Mark as Resolved
                  </>
                )}
              </MenuItem>
            )}
          </Menu>
        </Container>

        {/* Call Details Modal */}
        <Dialog 
          open={modalOpen} 
          onClose={handleCallModalClose} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <PhoneIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Call Details
                </Typography>
                {selectedCallId && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    ID: {selectedCallId}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: 0 }}>
            {callDetailsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={40} />
              </Box>
            ) : callDetails ? (
              <Box>
                {/* Quick Stats Section */}
                <Box sx={{ bgcolor: 'grey.50', p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                        <PhoneIcon sx={{ color: 'primary.main', fontSize: 28, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Phone Number
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {callDetails.phoneNumber}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                        <TimeIcon sx={{ color: 'info.main', fontSize: 28, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Duration
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatDuration(callDetails.duration)}
                        </Typography>
                      </Card>
                    </Grid>
                    {(userRole === 'super_admin' || userRole === 'admin') && (
                      <Grid item xs={12} sm={3}>
                        <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                          <MoneyIcon sx={{ color: 'success.main', fontSize: 28, mb: 1 }} />
                          <Typography variant="caption" color="text.secondary" display="block">
                            Total Cost
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {formatCost(callDetails.totalCost)}
                          </Typography>
                        </Card>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={3}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                        <PlayIcon sx={{ color: 'warning.main', fontSize: 28, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Recording
                        </Typography>
                        {callDetails.recordingUrl ? (
                          <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 500 }}>
                            Available
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Not Available
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                </Box>

                {/* Content Sections */}
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Audio Recording Section */}
                    {callDetails.recordingUrl && (
                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                              <PlayIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Call Recording
                            </Typography>
                          </Box>
                          <audio 
                            controls 
                            style={{ width: '100%' }}
                            preload="metadata"
                          >
                            <source src={callDetails.recordingUrl} type="audio/wav" />
                            <source src={callDetails.recordingUrl} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                        </Card>
                      </Grid>
                    )}

                    {/* Structured Data Section */}
                    {callDetails.structuredData && (
                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                              <DataIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Structured Data
                            </Typography>
                          </Box>
                          <TableContainer component={Paper} variant="outlined">
                            <Table sx={{ minWidth: 650 }}>
                              <TableBody>
                                {Object.entries(callDetails.structuredData).map(([key, value]) => (
                                  <TableRow key={key} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                                    <TableCell 
                                      component="th" 
                                      scope="row" 
                                      sx={{ 
                                        fontWeight: 600,
                                        minWidth: 200,
                                        borderRight: 1,
                                        borderColor: 'divider'
                                      }}
                                    >
                                      {formatFieldName(key)}
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word' }}>
                                      {value || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Card>
                      </Grid>
                    )}

                    {/* Summary Section */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                            <SummaryIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Call Summary
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          {callDetails.summary || 'No summary available for this call.'}
                        </Typography>
                      </Card>
                    </Grid>

                    {/* Transcript Section */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                            <TranscriptIcon />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Call Transcript
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          maxHeight: 400, 
                          overflow: 'auto',
                          bgcolor: 'grey.50',
                          p: 2,
                          borderRadius: 1
                        }}>
                          {callDetails.transcript ? (
                            <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {callDetails.transcript}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No transcript available for this call.
                            </Typography>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  Call details not found
                </Typography>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCallModalClose} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default FeedbackManagement; 